import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AvailableLanguages, defaultLanguages } from '@constants';
import { TranslateService } from '@ngx-translate/core';
import { DeviceInfoService } from '@core/services/device-info.service';
import { CommonsFacade } from '@state/commons';
import {
  BehaviorSubject,
  combineLatest,
  delay,
  delayWhen,
  distinctUntilChanged,
  map,
  of,
  ReplaySubject,
  startWith,
  take,
  takeUntil,
} from 'rxjs';
import {
  DataProcessingValidator,
  GDPRValidator,
  newAccountFormFields,
  NewAccountFormGroup,
  wholeFormValidator,
} from './new-account-form.config';

import { LocalStorageService } from '@core/services/local-storage.service';
import { AllDeviceInfo, Language, SelfRegisterOpts } from '@interfaces';
import { SystemSettingsFacade } from '@state/system-settings';
import { UsersFacade } from '@state/users';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';

@Component({
  selector: 'app-new-account',
  templateUrl: './new-account.component.html',
  styleUrls: [
    './new-account.component.scss',
    './new-account-desktop-shell.component.scss',
    './new-account-mobile-shell.component.scss',
  ],
  standalone: false,
})
export class NewAccountComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly usersFacade = inject(UsersFacade);
  private readonly commonsFacade = inject(CommonsFacade);
  private readonly systemSettingsFacade = inject(SystemSettingsFacade);
  private readonly deviceInfoService = inject(DeviceInfoService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly translateService = inject(TranslateService);
  private readonly fb = inject(FormBuilder);
  private readonly ls = inject(LocalStorageService);
  private readonly stackedMessage = inject(StackedMessagesService);

  private readonly destroy$: ReplaySubject<boolean> = new ReplaySubject(1);
  private readonly apiAvailable$ = new BehaviorSubject<boolean>(true);
  private readonly viewDataReady$ = new BehaviorSubject<boolean>(false);

  @ViewChild('mainTitleText') mainTitleText!: ElementRef;

  public APIAvailable = true;
  public selfRegisterActive: boolean;
  public deviceInfo: AllDeviceInfo;
  public isDesktopLayout = false;
  public currentUserLang: AvailableLanguages;
  public viewLoading = true;

  private readonly VIEW_LOADING_MIN_DURATION_MS = 220;

  public actionLoading$ = this.usersFacade.loading$.pipe(
    delayWhen(loading => (loading ? of(true) : of(false).pipe(delay(this.VIEW_LOADING_MIN_DURATION_MS)))),
    startWith(false),
    takeUntil(this.destroy$)
  );
  public loadingSelfRegisterActive$ = this.systemSettingsFacade.loading$.pipe(
    delayWhen(loading => (loading ? of(true) : of(false).pipe(delay(this.VIEW_LOADING_MIN_DURATION_MS)))),
    startWith(true),
    takeUntil(this.destroy$)
  );
  public loadingLangs$ = this.commonsFacade.loading$.pipe(
    delayWhen(loading => (loading ? of(true) : of(false).pipe(delay(this.VIEW_LOADING_MIN_DURATION_MS)))),
    startWith(true),
    takeUntil(this.destroy$)
  );
  // public actionLoading$ = of(true);
  // public loadingSelfRegisterActive$ = of(true);

  public errorMessage$ = this.usersFacade.errorMessage$;

  public newAccountForm: FormGroup<NewAccountFormGroup>;
  public registrationFinished = false;
  public lastUsedData: SelfRegisterOpts;

  public labelW = { xs: 14, sm: 10 };
  public inputW = { xs: 20, sm: 14, xl: 14 };

  public termsAcceptanceShownFully = false;

  private viewReadyTimer: ReturnType<typeof setTimeout> | null = null;

  public ngOnInit(): void {
    combineLatest([this.apiAvailable$, this.viewDataReady$, this.loadingLangs$, this.loadingSelfRegisterActive$])
      .pipe(
        map(
          ([APIAvailable, viewDataReady, loadingLangs, loadingSelfRegisterActive]) =>
            APIAvailable && (!viewDataReady || loadingLangs || loadingSelfRegisterActive)
        ),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(loading => this.setViewLoading(loading));

    this.commonsFacade.getLanguagesError$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.stackedMessage.showError({ message: 'COMMON.ERROR.ERROR_LOADING_DATA' });
      this.APIAvailable = false;
      this.apiAvailable$.next(false);
    });

    this.commonsFacade
      .getLanguages$()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(availableLanguages => {
        const { referalCode } = this.activatedRoute.snapshot.queryParams;
        // setup user lang with languages fetched from API
        this.setupUserLang([...availableLanguages]);

        this.newAccountForm = this.fb.group<NewAccountFormGroup>(newAccountFormFields, {
          validators: [wholeFormValidator, GDPRValidator, DataProcessingValidator],
          // updateOn: 'blur',
        });
        this.newAccountForm.get('langKey').setValue(this.translateService.currentLang);
        if (referalCode) {
          this.newAccountForm.patchValue({ referalCode });
        }
        this.viewDataReady$.next(true);
      });

    this.setDeviceInfo(this.deviceInfoService.getInfo());
    this.commonsFacade.lastAPIPingSuccessfull$.pipe(takeUntil(this.destroy$)).subscribe(APIPingSuccess => {
      if (!APIPingSuccess) {
        // if API connection failed - setup langs with default Languages hardcoded
        this.setupUserLang([...defaultLanguages]);
        this.APIAvailable = false;
        this.selfRegisterActive = false;
        this.apiAvailable$.next(false);
        return;
      }
      this.APIAvailable = true;
      this.apiAvailable$.next(true);
    });

    this.systemSettingsFacade.selfRegisterActive$.pipe(takeUntil(this.destroy$)).subscribe(isActive => {
      this.selfRegisterActive = isActive;
    });

    this.systemSettingsFacade.getSelfRegistrationStatus();
  }

  public ngAfterViewInit(): void {
    this.mainTitleText?.nativeElement.focus();

    this.usersFacade.selfRegisterSuccess$.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
      this.registrationFinished = true;
    });
    this.errorMessage$.pipe(takeUntil(this.destroy$)).subscribe(errorMessage => {
      if (errorMessage === 'ACCOUNT_WITH_SUCH_EMAIL_EXIST') {
        this.newAccountForm.get('email').setErrors({ emailTaken: true }, { emitEvent: true });
        this.newAccountForm.get('email').markAsDirty();
        this.newAccountForm.updateValueAndValidity();
      }

      if (errorMessage === 'SELF_REGISTRATION_CURRENTLY_UNAVAILABLE') {
        this.newAccountForm.get('referalCode').setErrors({ serverError: true }, { emitEvent: true });
        this.newAccountForm.get('referalCode').markAsDirty();
        this.newAccountForm.updateValueAndValidity();
      }
    });
  }

  public ngOnDestroy(): void {
    this.clearViewReadyTimer();
    this.registrationFinished = false;
    this.newAccountForm?.reset();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public userLangChanged($event: Language): void {
    this.currentUserLang = $event.key as AvailableLanguages;
    this.newAccountForm?.get('langKey')?.setValue(this.currentUserLang);
  }

  public sendForm(): void {
    // Do not allow to send multiple registration requests at 1 session
    // IDK how user might be able to manage to do that but just in case.
    if (this.registrationFinished) {
      return;
    }
    this.newAccountForm.markAllAsTouched();
    this.newAccountForm.updateValueAndValidity();

    const { email, langKey, name, surname, telephoneNumber, hasAcceptedGDPR, hasAcceptedDataProcessing } =
      this.newAccountForm.value;
    const selfRegisterOpts: SelfRegisterOpts = {
      email: email.trim(),
      langKey,
      name: `${name.trim()} ${surname.trim()}`.trim(),
      telephoneNumber: telephoneNumber.trim(),
      hasAcceptedGDPR,
      hasAcceptedDataProcessing,
    };

    this.lastUsedData = { ...selfRegisterOpts };
    this.usersFacade.selfRegister(selfRegisterOpts);
  }

  private setupUserLang(langs: Language[]): void {
    const { langKey } = this.activatedRoute.snapshot.queryParams;

    // lang from query params is most important
    const langInQueryParams = langs.find(({ key }) => key === langKey);

    // if no lang in query params then localStorage
    const langInLS = this.ls.getUserLang();

    // if no lang in Query & no in LS use default
    const defaultLang = langs.find(({ key }) => key === 'en');

    // 1. langInQuery 2. langInLS 3. defaultLang
    const langToUse = langInQueryParams?.key ? langInQueryParams : langInLS?.key ? langInLS : defaultLang;

    this.ls.setUserLang(langToUse);
    this.translateService.use(langToUse.key);
    this.currentUserLang = langToUse.key as AvailableLanguages;
  }

  public scrollToInput(event: FocusEvent) {
    if (this.deviceInfo.deviceTypeDetected === 'PHONE') {
      const target = event.target as HTMLElement;
      setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    }
  }

  private setDeviceInfo(info: AllDeviceInfo): void {
    this.deviceInfo = info;
    this.isDesktopLayout = info.deviceTypeDetected === 'DESKTOP';
  }

  private setViewLoading(loading: boolean): void {
    if (loading) {
      this.clearViewReadyTimer();
      this.viewLoading = true;
      return;
    }

    this.scheduleViewReady();
  }

  private scheduleViewReady(): void {
    if (this.viewReadyTimer || !this.viewLoading) {
      return;
    }

    this.viewReadyTimer = setTimeout(() => {
      this.viewLoading = false;
      this.viewReadyTimer = null;
    });
  }

  private clearViewReadyTimer(): void {
    if (!this.viewReadyTimer) {
      return;
    }

    clearTimeout(this.viewReadyTimer);
    this.viewReadyTimer = null;
  }
}
